import { Component, effect, inject, OnInit, signal, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { NotificationTemplate } from '../notification-template/notification-template';
import { AddEmail } from '../add-email/add-email';
import { DatePipe } from '@angular/common';

/*** imports for table filters componets ***/
import { TableComponent } from '../../shared/tablefilters/table/table.component';
import { TableFilterService } from '../../shared/tablefilters/table-filter.service';
import { Column } from '../../shared/tablefilters/column.model';
import { TableDetails } from '../../shared/tablefilters/table-details.model';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { Notification } from '../../../../../services/notification';
import { Exception, ToasterService } from '@anddone/ui-utilities';
import { Loader } from '../../../../layout/loader/loader';
import { MessageConstants } from '../../../../../constants/message.constant';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-notification-details',
  imports: [TableComponent, NgbDropdownModule, Loader, TranslateModule],
  templateUrl: './notification-details.html',
  styleUrl: './notification-details.scss',
  providers: [DatePipe],
})
export class NotificationDetails implements OnInit {
  allowViewTemplate = true;
  allowEditTemplate = false;
  viewTemplateModalRef: any;
  @ViewChild('removeEmailModal') removeEmailModal!: TemplateRef<any>;

  /*** Local variables needed for table filter system ***/
  searchParamsData: any = {};
  isFirstLoad = true;
  pager: any = {};
  tableActionSub!: Subscription;
  searchInputSub!: Subscription;
  private destroy$ = new Subject<void>();
  @ViewChild('removeEmailCell', { static: true })
  removeEmailCell!: TemplateRef<any>;
  @ViewChild('noEmailsModal') noEmailsModal!: TemplateRef<any>;
  emailToRemove: any;
  loggedInUserData: any = {};
  notificationId!: string;
  isLoader = signal<boolean>(false);
  notificationDetails: any;
  distributionId: any;
  updatedNotificationStatus = false;

  /*** Defined table details & columns ****/
  tableDetails: TableDetails = {
    tableId: 'distribution-list-table',
    searchPlaceholder: '',
    allowRowClick: false,
    tableTitle: 'Distribution List',
    defaultItemPerPage: 10,
    sendDefault90DayFilter: false,
  };
  columns: Column[] = [
    {
      name: 'Email',
      sortState: 'desc',
      isSortable: true,
      display: 'normal',
      responseKey: 'email',
      requestKey: 'Email',
      filter: {
        type: 'email',
        validation: ['required'],
      },
    },
    {
      name: 'Remove',
      sortState: 'none',
      isSortable: false,
      display: 'template',
      responseKey: 'operations',
      requestKey: null,
      filter: undefined,
      cellTemplate: this.removeEmailCell,
    },
  ];
  private router = inject(Router);
  private modalService = inject(NgbModal);
  private tableFilterService = inject(TableFilterService);
  private allyCommonService = inject(AllyCommonService);
  private activatedRouter = inject(ActivatedRoute);
  private notificationService = inject(Notification);
  private toasterService = inject(ToasterService);
  constructor() {
    effect(() => {
      const emailAddedCount = this.notificationService.emailAdded();
      const emailUpdatedCount = this.notificationService.emailUpdated();
      if (emailAddedCount > 0 || emailUpdatedCount > 0) {
        this.getNotificationDetails();
      }
    });
  }

  ngOnInit(): void {
    this.loggedInUserData = this.allyCommonService.getLoggedInData() || '';
    this.activatedRouter.params.subscribe((params) => {
      this.notificationId = params['id'];
    });

    this.getNotificationDetails();
    /*** Table Filter subscriptions ***/
    this.tableFilterService.init(this.columns, this.tableDetails);

    this.tableActionSub = this.tableFilterService.tableActionEvent.subscribe((_operationObject) => {
      // this.onUserOperationClick( operationObject.operation, operationObject.row );
    });

    this.searchInputSub = this.tableFilterService.searchInput
      .pipe(takeUntil(this.destroy$))
      .pipe(takeUntil(this.destroy$))
      .subscribe((searchInput) => {
        this.searchParamsData = searchInput.searchParams;
        this.pager = searchInput.pager;

        this.loadDistributionListIfReady(this.pager?.currentPage);
      });

    this.tableFilterService.registerSubscriptions(this.searchInputSub, this.tableActionSub);
    /*** End of Table filter subscription handling ***/
    const col = this.columns.find((c) => c.display === 'template');
    if (col) col.cellTemplate = this.removeEmailCell;
  }

  backToNotifications(): void {
    this.router.navigate(['/ally/notifications']);
  }

  closeModal() {
    this.modalService.dismissAll();
  }

  viewTemplate() {
    this.viewTemplateModalRef = this.modalService.open(NotificationTemplate, { scrollable: true });
    this.viewTemplateModalRef.componentInstance.notificationId = this.notificationId;
  }
  openDistributionListModal() {
    this.closeModal();
    this.modalService.open(AddEmail);
  }
  openAddEmailModal() {
    this.viewTemplateModalRef = this.modalService.open(AddEmail, { scrollable: true });
  }

  openRemoveEmailModal(row: any) {
    this.emailToRemove = row.email;
    this.viewTemplateModalRef = this.modalService.open(this.removeEmailModal, { scrollable: true });
  }

  removeEmailFromList() {
    this.isLoader.set(true);
    const obj = {
      emailId: this.emailToRemove,
    };
    this.notificationService
      .deleteDistributionEmail(obj, this.notificationId, this.distributionId)
      .subscribe({
        next: () => {
          this.isLoader.set(false);
          this.toasterService.showSuccess(MessageConstants.notification.emailsRemoved, 'full');
          this.getNotificationDetails();
        },
        error: (e: any) => {
          this.isLoader.set(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
    this.closeModal();
  }

  loadDistributionListIfReady(pageNumber?: number): void {
    if (!this.distributionId) {
      return;
    }

    this.getDistributionListwithPagination(pageNumber ?? this.pager?.currentPage ?? 1);
  }

  getNotificationDetails() {
    this.isLoader.set(true);

    this.notificationService
      .getNotificationEventDetails(this.loggedInUserData?.id, this.notificationId)
      .subscribe({
        next: (res: any) => {
          this.isLoader.set(false);
          this.notificationDetails = res;
          this.notificationService.setNotificationRow(this.notificationDetails);

          if (res.distributionList != null) {
            this.distributionId = res?.distributionList?.id;

            this.loadDistributionListIfReady(this.pager?.currentPage);
          }
        },
        error: (e: any) => {
          this.isLoader.set(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }

  getDistributionListwithPagination(pageNumber: number) {
    this.isLoader.set(true);
    if (this.isFirstLoad) {
      this.searchParamsData.SortField = 'Email';
      this.searchParamsData.Asc = false;
      this.isFirstLoad = false;
    }
    this.searchParamsData = this.tableFilterService.prepareSearchParamsForAPICall(pageNumber);
    this.notificationService
      ?.getDistributionListWithPagination(
        this.searchParamsData,
        this.notificationId,
        this.distributionId
      )
      ?.subscribe({
        next: (res: any) => {
          this.isLoader.set(false);
          if (res.emailList.length > 0) {
            res.data = res.emailList;
            res.totalRowCount = res.emailCount;
            res.data = res?.emailList.map((item: string) => ({
              email: item,
              operations: [{ key: 'remove', value: 'Remove' }],
            }));
            this.pager = this.tableFilterService.setPager(res, pageNumber, this.pager);
            this.tableDetails.tableData = res.data || [];
            this.tableFilterService.setTableDetails(this.tableDetails);
          } else {
            this.tableDetails.tableData = [];
            this.tableFilterService.setTableDetails(this.tableDetails);
          }
        },
        error: (e) => {
          this.isLoader.set(false);
          const toastMessage = Exception.exceptionMessage(e);
          this.toasterService.showError(toastMessage.join(', '), 'full');
        },
      });
  }

  subscribeDefaultNotification() {
    this.isLoader.set(true);
    this.notificationService.subscribeDefaultNotification(this.notificationId).subscribe({
      next: () => {
        this.isLoader.set(false);
        this.toasterService.showSuccess(MessageConstants.notification.subscribed, 'full');
        this.getNotificationDetails();
      },
      error: (e) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }

  subscribeAllyNotification(item: any) {
    this.updatedNotificationStatus = item?.currentTarget?.checked;
    this.isLoader.set(true);
    const obj = {
      status: item?.currentTarget?.checked ? 'Active' : 'InActive',
    };
    this.notificationService.subscribeAllyNotification(this.notificationId, obj).subscribe({
      next: () => {
        this.isLoader.set(false);
        if (this.updatedNotificationStatus) {
          this.toasterService.showSuccess(MessageConstants.notification.subscribed, 'full');
        } else if (!this.updatedNotificationStatus) {
          this.toasterService.showSuccess(MessageConstants.notification.unsubscribed, 'full');
        }
        this.getNotificationDetails();
      },
      error: (e) => {
        this.isLoader.set(false);
        const toastMessage = Exception.exceptionMessage(e);
        this.toasterService.showError(toastMessage.join(', '), 'full');
      },
    });
  }
  toggleStatus(statusToggle: HTMLInputElement) {
    if (
      this.notificationDetails?.distributionList == null ||
      !this.notificationDetails?.distributionList?.emailCount ||
      this.notificationDetails?.distributionList?.emailCount < 1
    ) {
      statusToggle.checked = false;
      this.modalService.open(this.noEmailsModal);
      return;
    } else {
      if (this.notificationDetails?.status?.toLowerCase() === 'default')
        this.subscribeDefaultNotification();
      else this.subscribeAllyNotification({ currentTarget: statusToggle });
    }
  }
}
